import React, { useEffect, useRef } from "react";

import styles from "../../../styles/patient-page-modules/requestDetailModal.module.css";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import Button from "../../button-v1";
import Spinner from "../../spinner";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createTicketEvent,
  getTicketEvents,
  getOrderedDevices,
  getAssignedDevices,
} from "../../../services/patient/devices";
import { getPatientGeneralDetails } from "../../../services/patient/details";
import toast from "react-hot-toast";
import { formatDate } from "../../../utils/helper";

const schema = {
  log_description: "",
};
const schemaValidation = z.object({
  log_description: z.string().min(1, "description is required"),
});
const RequestDetailModal = ({
  isOpen,
  onClose,
  currentPatient,
  requestData,
  ticket,
}) => {
  const queryClient = useQueryClient();
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({
    defaultValues: schema,
    resolver: zodResolver(schemaValidation),
  });

  const {
    data: patient,
    error,
    isLoading: isPatientLoading,
    refetch,
  } = useQuery(["patient-general-details"], () => getPatientGeneralDetails());

  const { data: orderedDevices, isLoading: isOrderedDevicesLoading } = useQuery(
    ["patient-ordered-devices"],
    () => getOrderedDevices()
  );

  const { data: assignedDevices } = useQuery(["patient-assigned-devices"], () =>
    getAssignedDevices()
  );

  const { data: events, isLoading: isEventLoading } = useQuery(
    ["technical-support-ticket-events"],
    () => getTicketEvents(ticket?.id)
  );

  const { mutate: addEventMutation, isLoading: isAddEventLoading } =
    useMutation((data) => createTicketEvent(ticket?.id, data), {
      onSuccess: () => {
        toast.success("Event added");
        queryClient.invalidateQueries(["technical-support-ticket-events"]);
      },
    });

  const handleSubmitData = (values) => {
    addEventMutation(values);
  };
  return (
    <Dialog open={isOpen} setOpen={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ticket Events</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {isEventLoading ? (
            <p className="content-center">
              <Spinner />
            </p>
          ) : (
            <div className={styles.request_wrapper}>
              <div className={styles.request_header}>
                <div className={styles.info}>
                  <h4>Patient Information</h4>
                  <div className={styles.details}>
                    <div>
                      <h5>Name</h5>
                      <p>
                        {patient?.user?.first_name} {patient?.user?.last_name}
                      </p>
                    </div>
                    <div>
                      <h5>Time Zone</h5>
                      <p>UTC +5</p>
                    </div>
                    <div>
                      <h5>Email</h5>
                      <p>{patient?.user?.email}</p>
                    </div>
                    <div>
                      <h5>Phone</h5>
                      <p>{patient?.user?.primary_phone}</p>
                    </div>
                  </div>
                </div>

                <div className={styles.info}>
                  <h4>Device Information</h4>
                  <div className={styles.details}>
                    <div>
                      <h5>Gateway ID / IMEI</h5>
                      <p>
                        {" "}
                        {
                          orderedDevices?.find(
                            (device) => device.id === ticket.ordered_device
                          )?.imei
                        }
                        {
                          assignedDevices?.find(
                            (device) => device.id === ticket.assigned_device
                          )?.imei
                        }
                      </p>
                    </div>
                    <div>
                      <h5>Device Type</h5>
                      <p>
                        {
                          orderedDevices?.find(
                            (device) => device.id === ticket.ordered_device
                          )?.name
                        }
                        {
                          assignedDevices?.find(
                            (device) => device.id === ticket.assigned_device
                          )?.name
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.request_header}>
                <form className={styles.addEventFormContainer}>
                  <h4>Add Event</h4>
                  <section>
                    <section className={styles.input_wrapper}>
                      <div className={styles.input_group}>
                        <div className={styles.input_field}>
                          <label>Event Description</label>
                          <input
                            type="text"
                            name="log_description"
                            {...register("log_description")}
                          />
                          {/* <span className="error-message">
                            {errors?.log_description?.message ?? ""}
                          </span> */}
                        </div>
                      </div>
                    </section>

                    <section>
                      <Button
                        size="default"
                        onClick={handleSubmit(handleSubmitData)}
                        loading={isAddEventLoading}
                        disabled={isAddEventLoading}
                      >
                        Add
                      </Button>
                    </section>
                  </section>
                </form>
              </div>

              <div className={styles.event_logs}>
                <div>
                  <h5>Description</h5>
                  <p>{requestData?.description}</p>
                </div>

                <div className={styles.logs}>
                  <h3>Event Logs</h3>
                  <div className={styles.table_container}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th className={styles.tableHeader}>Crreated By</th>
                          <th className={styles.tableHeader}>Time Stamp</th>
                          <th className={styles.tableHeader}>Discription</th>
                        </tr>
                      </thead>
                      <tbody>
                        {events?.map((event) => (
                          <tr>
                            <td className={styles.tableCell}>
                              {event?.created_by ?? "_"}
                            </td>
                            <td className={styles.tableCell}>
                              {formatDate(event?.created_at)}
                            </td>
                            <td className={styles.tableCell}>
                              {event?.log_description}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <p className={styles.current_status}>
                  <b>Current Status :</b>{" "}
                  {ticket?.resolved
                    ? "Resolved"
                    : ticket?.closed
                    ? "Closed"
                    : "Open"}
                </p>
              </div>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose()}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RequestDetailModal;
